import type { ScoringResult } from '@mathflat/domain/Scoring'
import type { AssignedWorksheetProblemDetail } from '@mathflat/domain/StudentWorksheet'
import { makeObservable, observable } from 'mobx'
import { EMPTY_NUMBER_ID } from 'src/constant/common'
import { UndoableDTO } from 'src/dto/UndoableDTO'

import { typeSafeAssign } from '@/utils/common'

type _Problem = AssignedWorksheetProblemDetail['problem']

// 문항 칸(채점 기록)과 그 문제를 한 겹으로 펼쳐 받는다. id 는 학습지 문제 id, problemId 는 문제은행 문제 id 다
type AdapterInterface = Pick<
  AssignedWorksheetProblemDetail,
  'result' | 'userAnswer' | 'handwrittenNoteUrl'
> &
  Pick<
    _Problem,
    | 'index'
    | 'favorite'
    | 'type'
    | 'answer'
    | 'answerData'
    | 'keypadTypes'
    | 'problemImageUrl'
    | 'answerImageUrl'
    | 'solutionImageUrl'
    | 'video'
    | 'conceptName'
    | 'tagTop'
    | 'level'
    | 'answerUnits'
    | 'videoMatched'
    | 'conceptId'
  > &
  Partial<Pick<_Problem, 'sourceType'>> & {
    id: AssignedWorksheetProblemDetail['worksheetProblemId']
    problemId: _Problem['id']
    virtualResult?: ScoringResult
    virtualUserAnswer?: string
  }

export class StudentWorksheetProblem extends UndoableDTO implements AdapterInterface {
  id = EMPTY_NUMBER_ID // StudentWorksheetProblem의 id
  /** 서버 원본이 아니라 다중 셀(userAnswers)까지 합친 표시용 KaTeX 문자열(formatSubmittedAnswer). 채점 비교에 쓰지 않는다 */
  userAnswer = ''
  problemId = EMPTY_NUMBER_ID // StudentWorksheetProblem 내부 Problem DTO의 id
  index = EMPTY_NUMBER_ID
  favorite = false
  answer = ''
  answerData = null as AdapterInterface['answerData']
  keypadTypes = [] as AdapterInterface['keypadTypes']
  problemImageUrl = ''
  answerImageUrl = ''
  solutionImageUrl = ''
  handwrittenNoteUrl = null
  result = 'CORRECT' as AdapterInterface['result']
  type = 'MULTIPLE_CHOICE' as AdapterInterface['type']
  video = null as AdapterInterface['video']
  conceptName = ''
  tagTop = ''
  level = 1 as AdapterInterface['level']
  answerUnits = [] as AdapterInterface['answerUnits']
  virtualResult: ScoringResult = 'NONE'
  /** userAnswer 와 같은 표시용 가공값(가채점) */
  virtualUserAnswer = ''
  videoMatched = true
  conceptId = EMPTY_NUMBER_ID
  sourceType: AdapterInterface['sourceType'] = undefined

  constructor(data?: AdapterInterface) {
    super()
    makeObservable(this, {
      id: observable,
      userAnswer: observable,
      result: observable,
      problemId: observable,
      index: observable,
      favorite: observable,
      answer: observable,
      answerData: observable,
      keypadTypes: observable,
      problemImageUrl: observable,
      answerImageUrl: observable,
      solutionImageUrl: observable,
      handwrittenNoteUrl: observable,
      type: observable,
      video: observable,
      conceptName: observable,
      tagTop: observable,
      level: observable,
      answerUnits: observable,
      virtualResult: observable,
      virtualUserAnswer: observable,
      videoMatched: observable,
      conceptId: observable,
      sourceType: observable,
    })

    if (data) typeSafeAssign(this, data)
  }
}
